import { logger } from '@/core/logging/logger'

/**
 * Optional feedback tones for marking and erasing.
 *
 * Synthesised with WebAudio rather than shipped as audio files: two short
 * blips would be a few KB of assets and a decode, for something off by
 * default. A pair of oscillator envelopes costs nothing until used.
 *
 * This exists because there are no haptics available (CLAUDE.md rule 11), so
 * visual and optional audio carry the whole feedback load.
 */

export type SoundKind = 'mark' | 'erase'

/** Created lazily. Constructing an AudioContext before a gesture is refused. */
let context: AudioContext | null = null
let unavailable = false

function ensureContext(): AudioContext | null {
  if (unavailable) return null
  if (context) return context

  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext

  if (!Ctor) {
    unavailable = true
    logger.info('timeline', 'WebAudio unavailable; sound feedback disabled')
    return null
  }

  try {
    context = new Ctor()
    return context
  } catch (err) {
    unavailable = true
    logger.warn('timeline', 'Could not create an AudioContext', err)
    return null
  }
}

const TONES: Record<SoundKind, { frequency: number; duration: number }> = {
  // A short, high, quiet tick. Deliberately not a "ding" — this can fire
  // forty times while filling a day, and anything musical becomes maddening.
  mark: { frequency: 880, duration: 0.045 },
  // Lower, so erase is distinguishable from mark without looking.
  erase: { frequency: 320, duration: 0.06 },
}

/**
 * Plays one blip. Never throws, never awaits — the caller is in a tap handler
 * and the write to IndexedDB must not wait on audio.
 */
export function playSound(kind: SoundKind): void {
  const ctx = ensureContext()
  if (!ctx) return

  try {
    // Safari suspends the context whenever the app is backgrounded. Resuming
    // is safe to call repeatedly and is a no-op when already running.
    if (ctx.state === 'suspended') void ctx.resume()

    const { frequency, duration } = TONES[kind]
    const now = ctx.currentTime

    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.type = 'sine'
    osc.frequency.value = frequency

    // A hard start or stop on an oscillator is an audible click. Ramping the
    // gain in and out over a few milliseconds removes it.
    gain.gain.setValueAtTime(0, now)
    gain.gain.linearRampToValueAtTime(0.07, now + 0.005)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration)

    osc.connect(gain).connect(ctx.destination)
    osc.start(now)
    osc.stop(now + duration + 0.01)
  } catch (err) {
    logger.warn('timeline', 'Sound playback failed', err)
  }
}

/** Releases the audio context. Called when sound is switched off in settings. */
export function releaseSound(): void {
  if (!context) return
  void context.close().catch(() => undefined)
  context = null
}
